import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { API_CONFIG } from './constants/app';
import ErrorBoundary from "./components/ErrorBoundary";
import { LanguageProvider } from "./contexts/LanguageContext";
import { useLanguage } from './contexts/LanguageContext';
import { SUPPORTED_LANGUAGES } from "./data/supportedLanguages";
import { publicLocaleFromPath, SEO_LANGUAGES } from './data/publicSeo';
import Footer from "./components/ui/Footer";
import Header from "./components/ui/Header";
import CookieConsent from "./components/CookieConsent";
import { isAssessmentFlowPath, ROUTES } from './constants/routes';
import { logger } from './utils/core/dystestLogger';
import { useCookieConsent } from './hooks/useCookieConsent';
import { ensureGA4Loaded, trackPageView } from './services/analyticsService';
import { isMobileDevice } from './utils/ui/device';
import MobileGate from './components/ui/MobileGate';
import { FullscreenReturnGuard } from './components/ui/FullscreenPrompt';
import { isTestRun, syncTestRunFlagFromUrl } from './utils/testRun';
import { MIC_HOLD_ROUTES, releaseHeldMic } from './services/assessment/micHandoff';
import { restoreDraftAfterReload } from './services/assessmentDraftService';
import { setAssessmentTranslationProtection } from './utils/ui/assessmentTranslation';
import { useAssessmentTranslationProtection } from './hooks/useAssessmentTranslationProtection';

// A reload in the middle of the test empties the in-memory results (camera setup,
// Word-Icon, …). Put the browser's backup of this same assessment back before any
// page reads them. Runs once, before the first render.
if (typeof window !== 'undefined') {
  // Reassert the static HTML protection before React mounts any page content.
  setAssessmentTranslationProtection(true);
  if (isAssessmentFlowPath(window.location.pathname)) restoreDraftAfterReload();
}
const ParentFormPage = lazy(() => import("./pages/ParentFormPage"));
const AllowCameraPage = lazy(() => import("./pages/AllowCameraPage"));
const RANTestPage = lazy(() => import("./pages/RANTestPage"));
const CalibrationGame = lazy(() => import("./pages/CalibrationGame"));
const Story1Page = lazy(() => import("./pages/Story1Page"));
const Story1Questions = lazy(() => import("./pages/Story1Questions"));
const Story2Page = lazy(() => import("./pages/Story2Page"));
const Story2Questions = lazy(() => import("./pages/Story2Questions"));
const FinishPage = lazy(() => import("./pages/FinishPage"));
const WordWritingTestPage = lazy(() => import("./pages/WordWritingTestPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const TermService = lazy(() => import("./pages/TermService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ContactSupport = lazy(() => import("./pages/ContactSupport"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const NewsletterPage = lazy(() => import("./pages/NewsletterPage"));
const TechnicalPapersPage = lazy(() => import("./pages/TechnicalPapersPage"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const TechnicalPaperPage = lazy(() => import("./pages/TechnicalPaperPage"));
const UserLoginPage = lazy(() => import("./pages/UserLoginPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminloginPage"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const WhiteLabelPage    = lazy(() => import("./pages/WhiteLabelPage"));
const TeacherLoginPage  = lazy(() => import("./pages/TeacherLoginPage"));
const TeacherDashboard  = lazy(() => import("./pages/TeacherDashboard"));
const ResumePage        = lazy(() => import("./pages/ResumePage"));
const ProtectedRoute    = lazy(() => import("./components/ProtectedRoute"));
const ChatBot           = lazy(() => import("./components/ChatBot"));
// Step 25: notice when the camera stood still; loads with the test pages only.
const CameraFreezeNotice = lazy(() => import("./components/ui/CameraFreezeNotice"));
// Marks the session abandoned, with the reason, when someone leaves the test.
const AssessmentExitWatcher = lazy(() => import("./components/AssessmentExitWatcher"));
const ConsentAnalytics  = lazy(() => import("@vercel/analytics/react").then((mod) => ({ default: mod.Analytics })));
const ConsentSpeedInsights = lazy(() => import("@vercel/speed-insights/react").then((mod) => ({ default: mod.SpeedInsights })));

const CHATBOT_PUBLIC_PATHS = new Set([
  '/',
  '/about',
  '/contact',
  '/faq',
  '/blog',
  '/guides',
  '/technical-papers',
  ROUTES.LOGIN,
  ROUTES.DASHBOARD,
  ROUTES.ADMIN_DASHBOARD,
]);

function shouldShowChatBot(pathname: string): boolean {
  return CHATBOT_PUBLIC_PATHS.has(pathname) || pathname.startsWith('/blog/');
}

// Component that handles conditional header rendering
function AppContent() {
  const location = useLocation();
  useAssessmentTranslationProtection(location.pathname);
  const { analytics: analyticsConsent } = useCookieConsent();
  const [observabilityReady, setObservabilityReady] = useState(false);
  const [supportWidgetReady, setSupportWidgetReady] = useState(false);

  useEffect(() => {
    if (!analyticsConsent) {
      setObservabilityReady(false);
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      ensureGA4Loaded();
      trackPageView(location.pathname);
      setObservabilityReady(true);
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [location.pathname, analyticsConsent]);

  const [testRun, setTestRun] = useState(isTestRun);
  useEffect(() => {
    syncTestRunFlagFromUrl(location.search);
    setTestRun(isTestRun());
  }, [location.search]);

  // The mic from the camera-setup check is kept (muted) only until RAN; leaving
  // that part of the flow any other way releases it.
  useEffect(() => {
    if (!MIC_HOLD_ROUTES.has(location.pathname)) releaseHeldMic();
  }, [location.pathname]);

  useEffect(() => {
    setSupportWidgetReady(false);
    if (!shouldShowChatBot(location.pathname)) return undefined;
    const timeout = window.setTimeout(() => setSupportWidgetReady(true), 5000);
    return () => window.clearTimeout(timeout);
  }, [location.pathname]);

  if (isMobileDevice() && isAssessmentFlowPath(location.pathname)) {
    return <MobileGate />;
  }

  // Paths where AppContent suppresses its own <Header />.
  // "/" is included because ParentFormPage renders its own <Header /> inside
  // its gradient background container — having AppContent also render one
  // would cause a double nav bar on the home page.
  const noHeaderPaths: string[] = [
    "/",
    ...SEO_LANGUAGES.filter((language) => language !== 'en').map((language) => `/${language}`),
    ROUTES.ALLOW_CAMERA,
    ROUTES.RAN_TEST,
    ROUTES.CALIBRATION,
    ROUTES.STORY,
    ROUTES.STORY1_QUESTIONS,
    ROUTES.STORY2,
    ROUTES.STORY2_QUESTIONS,
    ROUTES.WORD_WRITING,
    ROUTES.FINISH,
    ROUTES.LOGIN,
    ROUTES.ADMIN_LOGIN,
    ROUTES.DASHBOARD,
    ROUTES.ADMIN_DASHBOARD,
  ];

  // Paths where AppContent also suppresses its own <Footer />.
  // "/" is NOT in this list — the home page footer comes from AppContent.
  const noFooterPaths: string[] = [
    ROUTES.ALLOW_CAMERA,
    ROUTES.RAN_TEST,
    ROUTES.CALIBRATION,
    ROUTES.STORY,
    ROUTES.STORY1_QUESTIONS,
    ROUTES.STORY2,
    ROUTES.STORY2_QUESTIONS,
    ROUTES.WORD_WRITING,
    ROUTES.FINISH,
    ROUTES.LOGIN,
    ROUTES.ADMIN_LOGIN,
    ROUTES.DASHBOARD,
    ROUTES.ADMIN_DASHBOARD,
  ];

  // White-label pages (/:orgSlug) suppress both header and footer — they
  // render their own branded layout.  Detect by checking it's a single-segment
  // path that isn't any of our named routes.
  const namedPaths = new Set([
    '/', '/faq', '/terms-of-service', '/privacy-policy', '/contact',
    '/blog', '/about', '/guides', '/technical-papers', '/newsletter', ...Object.values(ROUTES),
    ...SEO_LANGUAGES.filter((language) => language !== 'en').map((language) => `/${language}`),
  ]);
  const segments = location.pathname.split('/').filter(Boolean);
  const isWhiteLabel = (
    segments.length === 1 && !namedPaths.has(location.pathname)
  ) || (
    segments.length === 3 && segments[1] === 'start' && !namedPaths.has(`/${segments[0]}`)
  );
  // Teacher portal paths (/:orgSlug/login and /:orgSlug/dashboard) are self-contained
  const isTeacherPortal = segments.length === 2 &&
    (segments[1] === 'login' || segments[1] === 'dashboard');

  const showMinimalHeader = noHeaderPaths.includes(location.pathname) || isWhiteLabel || isTeacherPortal;
  const hideFooter = noFooterPaths.includes(location.pathname) || isWhiteLabel || isTeacherPortal;

  return (
    <div className="flex flex-col min-h-screen">
      {/* WCAG 2.1 SC 2.4.1 — Skip navigation: visible on :focus, hidden otherwise */}
      <a href="#main-content" className="skip-nav">Skip to main content</a>

      {testRun && (
        <div
          role="status"
          className="pointer-events-none fixed bottom-2 left-2 z-[9999] rounded bg-amber-500 px-2 py-0.5 text-xs font-bold text-white shadow"
        >
          Test run
        </div>
      )}

      {/* Show FULL Header (with language) for most pages */}
      {!showMinimalHeader && <Header />}
      {/* NOTE: Pages in minimalHeaderPaths should use their own MiniHeader component */}

      <main id="main-content" className="flex-grow notranslate" translate="no" aria-label="Main content">
        <ErrorBoundary>
        <Suspense fallback={
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              border: '3px solid #e0e7ff', borderTopColor: '#6366f1',
              animation: 'spin 0.7s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        }>
        <Routes>
          {/* Pages with FULL Header */}
          <Route path="/" element={<ParentFormPage />} />
          {SEO_LANGUAGES.filter((language) => language !== 'en').map((language) => (
            <Route key={language} path={`/${language}`} element={<ParentFormPage />} />
          ))}
          <Route path="/faq" element={<FAQPage />} />
          {SEO_LANGUAGES.filter((language) => language !== 'en').map((language) => (
            <Route key={`${language}-faq`} path={`/${language}/faq`} element={<FAQPage />} />
          ))}
          <Route path="/terms-of-service" element={<TermService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<ContactSupport />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          <Route path="/technical-papers" element={<TechnicalPapersPage />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/papers/:id" element={<TechnicalPaperPage />} />
          <Route path="/about" element={<AboutPage />} />
          {SEO_LANGUAGES.filter((language) => language !== 'en').map((language) => (
            <Route key={`${language}-about`} path={`/${language}/about`} element={<AboutPage />} />
          ))}
          <Route path="/guides" element={<GuidePage />} />
          <Route path={ROUTES.LOGIN} element={<UserLoginPage />} />
          <Route path={ROUTES.ADMIN_LOGIN} element={<AdminLoginPage />} />
          <Route element={<ProtectedRoute type="user" />}>
            <Route path={ROUTES.DASHBOARD} element={<UserDashboard />} />
          </Route>
          <Route element={<ProtectedRoute type="admin" />}>
            <Route path={ROUTES.ADMIN_DASHBOARD} element={<AdminDashboard />} />
          </Route>

          {/* Pages with MINIMAL Header (they use MiniHeader inside the component) */}
          <Route path={ROUTES.ALLOW_CAMERA} element={<AllowCameraPage />} />
          <Route path={ROUTES.RAN_TEST} element={<RANTestPage />} />
          <Route path={ROUTES.CALIBRATION} element={<CalibrationGame />} />
          <Route path={ROUTES.STORY} element={<Story1Page />} />
          <Route path={ROUTES.STORY1_QUESTIONS} element={<Story1Questions />} />
          <Route path={ROUTES.STORY2} element={<Story2Page />} />
          <Route path={ROUTES.STORY2_QUESTIONS} element={<Story2Questions />} />
          <Route path={ROUTES.WORD_WRITING} element={<WordWritingTestPage />} />
          <Route path={ROUTES.FINISH} element={<FinishPage />} />
          <Route path={ROUTES.RESUME} element={<ResumePage />} />

          {/* Teacher portal — MUST be before /:orgSlug catch-all */}
          <Route path="/:orgSlug/login"      element={<TeacherLoginPage />} />
          <Route path="/:orgSlug/dashboard"  element={<TeacherDashboard />} />
          <Route path="/:orgSlug/start/:inviteToken" element={<WhiteLabelPage />} />

          {/* White-label catch-all — MUST be last so named routes take priority.
              dystest.vercel.app/:orgSlug → branded landing page for B2B partners */}
          <Route path="/:orgSlug" element={<WhiteLabelPage />} />

          {/* Anything else (old WordPress paths such as /comments/feed/) goes home, not to an empty page. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>

      <FullscreenReturnGuard />
      {isAssessmentFlowPath(location.pathname) && (
        <Suspense fallback={null}>
          <CameraFreezeNotice />
          <AssessmentExitWatcher />
        </Suspense>
      )}

      {!hideFooter && <Footer />}

      {/* ChatBot — public information/support pages, login, and dashboards */}
      {shouldShowChatBot(location.pathname) && supportWidgetReady && (
        <Suspense fallback={null}>
          <ChatBot />
        </Suspense>
      )}
      <CookieConsent />

      {/* Vercel observability — gated on analytics cookie consent so it never
          collects telemetry before the visitor has made (or refused) that choice. */}
      {analyticsConsent && observabilityReady && (
        <Suspense fallback={null}>
          <ConsentAnalytics />
          <ConsentSpeedInsights />
        </Suspense>
      )}
    </div>
  );
}

function PublicLocaleSync() {
  const location = useLocation();
  const { currentLanguage, changeLanguage } = useLanguage();
  useEffect(() => {
    const pathLanguage = publicLocaleFromPath(location.pathname);
    if (pathLanguage && pathLanguage !== currentLanguage) changeLanguage(pathLanguage);
  }, [location.pathname, currentLanguage, changeLanguage]);
  return null;
}

function App() {
  useEffect(() => {
    if (isMobileDevice()) return;
    // Warm-up ping + startup health check — keeps Render service from cold-starting
    const HEALTH_URL = API_CONFIG.BASE_URL.replace('/api/v1', '') + '/health';
    const ping = () =>
      fetch(HEALTH_URL)
        .then(async (res) => {
          if (!res.ok) {
            logger.warn(`Backend unhealthy (HTTP ${res.status})`);
          }
        })
        .catch((err) => {
          logger.warn('Backend unreachable on startup', err instanceof Error ? err : new Error(String(err)));
        });
    const warmupId = window.setTimeout(ping, 8000); // keep out of the LCP path
    const id = setInterval(ping, 10 * 60 * 1000); // repeat every 10 min
    return () => {
      window.clearTimeout(warmupId);
      clearInterval(id);
    };
  }, []);

  return (
    <LanguageProvider supportedLanguages={SUPPORTED_LANGUAGES}>
      <Router>
        <PublicLocaleSync />
        <AppContent />
      </Router>
    </LanguageProvider>
  );
}

export default App;
